import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  ViewChild
} from '@angular/core';
import {
  MatCalendar,
  MatCalendarCellClassFunction,
  MatCalendarView
} from '@angular/material/datepicker';
import { DateUtils } from '@shared/date-utils';

/**
 * Calendário mensal para frequência usando `mat-calendar` (Angular Material)
 * para alinhamento correto dos dias com os cabeçalhos da semana.
 */
@Component({
  selector: 'app-calendario-frequencia',
  templateUrl: './calendario-frequencia.component.html',
  styleUrls: ['./calendario-frequencia.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarioFrequenciaComponent implements OnChanges, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * O `MatCalendar` só aplica `[startAt]` na primeira renderização; ao mudar mês/ano no pai,
   * é preciso atualizar `activeDate`, senão a grade continua no mês anterior (ex.: MAR. com Abril no cabeçalho).
   */
  @ViewChild(MatCalendar) private matCalendar?: MatCalendar<Date>;

  @Input() mesAtual!: number;
  @Input() anoAtual!: number;
  @Input() diaSelecionado: number | null = null;
  /** Lista ISO `YYYY-MM-DD` dos dias letivos / com aula no mês (diária e por aula). */
  @Input() diasLetivosDoMes: string[] = [];
  @Input() periodoDataInicial: string | null = null;
  @Input() periodoDataFinal: string | null = null;

  @Output() diaSelecionadoChange = new EventEmitter<number>();

  selectedDate: Date | null = null;
  startAtDate = new Date();
  minDate: Date | null = null;
  maxDate: Date | null = null;

  /**
   * Filtro de datas selecionáveis (letivos no modo dia).
   * Referência nova a cada `rebuildDateFilterAndClass()` para o `MatCalendar` rodar `ngOnChanges` e `_init()` (#24435).
   */
  dateFilterFn: (d: Date | null) => boolean = () => false;

  /** Destaque visual para dias não letivos. */
  dateClassFn: MatCalendarCellClassFunction<Date> = () => '';

  ngOnChanges(): void {
    if (this.mesAtual === undefined || this.anoAtual === undefined) {
      return;
    }
    this.rebuildDateFilterAndClass();
    this.syncBounds();
    this.syncSelected();
    this.startAtDate = new Date(this.anoAtual, this.mesAtual, 1);
    this.cdr.markForCheck();
    // Após o próximo ciclo de view, o ViewChild existe e o Material aplicou inputs.
    queueMicrotask(() => this.syncMatCalendarActiveMonth());
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.syncMatCalendarActiveMonth());
  }

  /**
   * Alinha o mês exibido pelo `mat-calendar` com `mesAtual` / `anoAtual` do componente pai.
   * Sempre chama `updateTodaysDate()` para recriar células quando só `diasLetivosDoMes` muda (mesmo mês).
   */
  private syncMatCalendarActiveMonth(): void {
    if (this.mesAtual === undefined || this.anoAtual === undefined) {
      return;
    }
    const cal = this.matCalendar;
    if (!cal) {
      return;
    }
    const target = new Date(this.anoAtual, this.mesAtual, 1);
    const current = cal.activeDate;
    if (
      current.getFullYear() !== target.getFullYear() ||
      current.getMonth() !== target.getMonth()
    ) {
      cal.activeDate = target;
    }
    cal.updateTodaysDate();
    this.cdr.markForCheck();
  }

  /** Nova referência de filtro/classe para o Material reaplicar bloqueio visual aos dias. */
  private rebuildDateFilterAndClass(): void {
    this.dateFilterFn = (d: Date | null): boolean => {
      if (!d) {
        return false;
      }
      return this.diasLetivosDoMes.includes(DateUtils.formatarDataISO(d));
    };
    this.dateClassFn = (d: Date, view: MatCalendarView): string => {
      if (view !== 'month') {
        return '';
      }
      const iso = DateUtils.formatarDataISO(d);
      return this.diasLetivosDoMes.includes(iso) ? '' : 'cal-freq-nao-letivo';
    };
  }

  onSelectedChange(d: Date | null): void {
    if (!d) {
      return;
    }
    this.diaSelecionadoChange.emit(d.getDate());
  }

  private syncSelected(): void {
    if (this.diaSelecionado != null) {
      this.selectedDate = new Date(this.anoAtual, this.mesAtual, this.diaSelecionado);
    } else {
      this.selectedDate = null;
    }
  }

  private syncBounds(): void {
    const ultimoDia = new Date(this.anoAtual, this.mesAtual + 1, 0).getDate();
    let diaIni = 1;
    let diaFim = ultimoDia;
    if (this.periodoDataInicial && this.periodoDataFinal) {
      const pi = DateUtils.parseDateISO(this.periodoDataInicial);
      const pf = DateUtils.parseDateISO(this.periodoDataFinal);
      if (pi && this.anoAtual === pi.ano && this.mesAtual === pi.mes) {
        diaIni = pi.dia;
      }
      if (pf && this.anoAtual === pf.ano && this.mesAtual === pf.mes) {
        diaFim = pf.dia;
      }
    }
    this.minDate = new Date(this.anoAtual, this.mesAtual, diaIni);
    this.maxDate = new Date(this.anoAtual, this.mesAtual, diaFim);
  }
}
